import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [pageImages, setPageImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setIsVisible(true);
    fetchPageImages();
  }, []);

  const fetchPageImages = async () => {
    try {
      const response = await axios.get(`${API}/cms/page-images`);
      const imagesMap = {};
      response.data.forEach(img => {
        imagesMap[img.section] = img.url;
      });
      setPageImages(imagesMap);
    } catch (error) {
      console.error("Error fetching page images:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden grain-overlay">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${pageImages.hero_background || 'https://images.unsplash.com/photo-1638623144820-46fa0a85de28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBicmlkYWwlMjBtYWtldXAlMjBzb2Z0JTIwbGlnaHRpbmd8ZW58MHx8fHwxNzY3NDcxODAwfDA&ixlib=rb-4.1.0&q=85'})`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#F9F8F6]/95 via-[#F9F8F6]/80 to-transparent" />
        </div>

        {/* Content */}
        <div className={`relative z-10 max-w-7xl mx-auto px-6 md:px-12 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-sm tracking-widest text-[#8C8C8C] uppercase">Luxury Hair & Makeup Artistry</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight" data-testid="hero-heading">
              The Lux Standard
            </h1>
            
            <p className="text-lg sm:text-xl text-[#1A1A1A] mb-8 leading-relaxed max-w-xl">
              Consultation-driven precision for brides, events, and production environments. 
              Calm authority. Compliant excellence. Unforgettable results.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/inquiry"
                data-testid="hero-cta-button"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#b89530] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
              >
                Book a Consultation
                <ArrowRight className="w-5 h-5" />
              </Link>
              
              <Link
                to="/services"
                data-testid="hero-secondary-button"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#1A1A1A] text-[#1A1A1A] px-8 py-4 rounded-full text-base font-medium hover:bg-[#F2EBE5] transition-all duration-300"
              >
                View Services & Investment
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 md:py-32 bg-white" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">Our Services</h2>
            <p className="text-lg text-[#8C8C8C] max-w-2xl mx-auto">
              Elevated experiences tailored to your vision
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Bridal */}
            <div className="group" data-testid="service-card-bridal">
              <div className="relative overflow-hidden rounded-lg mb-6 h-80">
                <img
                  src={pageImages.services_bridal || "https://images.unsplash.com/photo-1736849603893-0457300abca5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBicmlkYWwlMjBtYWtldXAlMjBzb2Z0JTIwbGlnaHRpbmd8ZW58MHx8fHwxNzY3NDcxODAwfDA&ixlib=rb-4.1.0&q=85"}
                  alt="Bridal Services"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/60 to-transparent" />
              </div>
              <h3 className="text-2xl font-bold mb-3">Bridal</h3>
              <p className="text-[#8C8C8C] mb-4">
                Your wedding day deserves flawless artistry. From trials to the final reveal, we ensure you feel radiant.
              </p>
              <Link to="/services" className="text-[#D4AF37] font-medium inline-flex items-center gap-2 hover:gap-3 transition-all">
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Events */}
            <div className="group" data-testid="service-card-events">
              <div className="relative overflow-hidden rounded-lg mb-6 h-80">
                <img
                  src={pageImages.services_events || "https://images.unsplash.com/photo-1638623144820-46fa0a85de28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBicmlkYWwlMjBtYWtldXAlMjBzb2Z0JTIwbGlnaHRpbmd8ZW58MHx8fHwxNzY3NDcxODA1fDA&ixlib=rb-4.1.0&q=85"}
                  alt="Event Services"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/60 to-transparent" />
              </div>
              <h3 className="text-2xl font-bold mb-3">Events</h3>
              <p className="text-[#8C8C8C] mb-4">
                High-end gatherings, galas, and special occasions. Look your absolute best when it matters most.
              </p>
              <Link to="/services" className="text-[#D4AF37] font-medium inline-flex items-center gap-2 hover:gap-3 transition-all">
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Production */}
            <div className="group" data-testid="service-card-production">
              <div className="relative overflow-hidden rounded-lg mb-6 h-80">
                <img
                  src={pageImages.services_production || "https://images.unsplash.com/photo-1599151653682-a1cd712b1921?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxmaWxtJTIwcHJvZHVjdGlvbiUyMHNldCUyMGhhaXIlMjBtYWtldXAlMjBhcnRpc3R8ZW58MHx8fHwxNzY3NDcxODA1fDA&ixlib=rb-4.1.0&q=85"}
                  alt="Production Services"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/60 to-transparent" />
              </div>
              <h3 className="text-2xl font-bold mb-3">Production</h3>
              <p className="text-[#8C8C8C] mb-4">
                NDA-ready, OSHA-compliant artistry for film, photo shoots, and commercial projects.
              </p>
              <Link to="/production" className="text-[#D4AF37] font-medium inline-flex items-center gap-2 hover:gap-3 transition-all">
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 md:py-32 bg-[#F9F8F6]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">Client Testimonials</h2>
            <p className="text-lg text-[#8C8C8C] max-w-2xl mx-auto">
              Real experiences from brides, clients, and production professionals
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-[#F2EBE5]" data-testid="testimonial-card-1">
              <div className="mb-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-[#D4AF37]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-[#1A1A1A] italic mb-4 font-serif text-lg">
                  "Absolutely stunning! My wedding day makeup was flawless from start to finish. The consultation process made me feel heard and understood. Worth every penny."
                </p>
              </div>
              <div className="border-t pt-4">
                <p className="font-semibold text-[#1A1A1A]">Sarah Martinez</p>
                <p className="text-sm text-[#8C8C8C]">Bride, June 2025</p>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-[#F2EBE5]" data-testid="testimonial-card-2">
              <div className="mb-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-[#D4AF37]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-[#1A1A1A] italic mb-4 font-serif text-lg">
                  "Professional, punctual, and incredibly talented. They handled our entire bridal party with ease. Everyone looked amazing and the makeup lasted all night."
                </p>
              </div>
              <div className="border-t pt-4">
                <p className="font-semibold text-[#1A1A1A]">Emily Chen</p>
                <p className="text-sm text-[#8C8C8C]">Mother of the Bride, October 2024</p>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-[#F2EBE5]" data-testid="testimonial-card-3">
              <div className="mb-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-[#D4AF37]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-[#1A1A1A] italic mb-4 font-serif text-lg">
                  "We've worked with many makeup artists on set, but TheLuxStandard stands out. Compliant, professional, and the talent looked perfect on camera. Highly recommend for production work."
                </p>
              </div>
              <div className="border-t pt-4">
                <p className="font-semibold text-[#1A1A1A]">Marcus Rivera</p>
                <p className="text-sm text-[#8C8C8C]">Film Director, Las Vegas Productions</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#F2EBE5]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Ready to Experience The Lux Standard?
          </h2>
          <p className="text-lg text-[#8C8C8C] mb-8">
            Begin with a 20-30 minute consultation to ensure we deliver exactly what you envision.
          </p>
          <Link
            to="/inquiry"
            data-testid="cta-button"
            className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#b89530] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
          >
            Book Your Consultation
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;